import { cclFetchXSRF, createInternalAppError, generateUrlWithParams, isValidUrl, registerSessionExpiredListener, Undefinedable } from 'gocclr-shared-ui/dist/helpers';
import { AccountsContextData, AccountsUser, ApplicationPaths, HeaderFooterResource } from '../model/account-context-data';
import { getRawApplicationContextData } from '../services/initial-data.service';

const APP_TOKEN_HEADER_NAME = 'application-token';

let _data: Undefinedable<AccountsContextData> = undefined;

const isLocalhost = Boolean(
    window.location.hostname === 'localhost' ||
    window.location.hostname === 'bs-local.com' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.1/8 is considered localhost for IPv4.
    window.location.hostname.match(
        /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

function sessionExpiredHandler(): void {
    if (!isBookingContextReady()) {
        return;
    }

    const paths = getApplicationPaths();
    const loginUrl = generateUrlWithParams(paths.logout, { returnUrl: `${window.location.pathname}${window.location.search}` });

    if (!isValidUrl(loginUrl)) {
        return;
    }

    window.location.href = loginUrl;
}


export async function initBookingContext(forceLoad: boolean): Promise<boolean> {
    registerSessionExpiredListener(sessionExpiredHandler)
    const appContext = await getRawApplicationContextData(forceLoad);
    _data = { ...appContext };
    return true;
}

function assertIfBookingContextIsReady() {
    if (!isBookingContextReady()) {
        throw createInternalAppError('app context not initialized');
    }
}

function addApplicationTokenHeader(applicationToken: string, init?: RequestInit): RequestInit {
    if (!init) {
        return {
            headers: {
                [APP_TOKEN_HEADER_NAME]: applicationToken
            }
        };
    }
    const { headers, ...rest } = init;
    return {
        headers: {
            [APP_TOKEN_HEADER_NAME]: applicationToken,
            ...headers
        },
        ...rest
    };
}

export function isBookingContextReady(): boolean {
    return !!_data;
}

export function gocclFetch<TResponse>(input: RequestInfo, init?: RequestInit): Promise<TResponse> {
    if (!isBookingContextReady()) {
        throw createInternalAppError('app context not initialized');
    }

    return cclFetchXSRF<TResponse>(input, _data!.antiForgeryToken, addApplicationTokenHeader(_data!.applicationToken, init));
}

export function getApplicationBasePath(): string {
    assertIfBookingContextIsReady();
    return isLocalhost ? '/' : _data!.basePath;
}

export function getBasePathForMenu(): string {
    assertIfBookingContextIsReady();
    return _data!.basePath;
}

export function getApplicationPaths(): ApplicationPaths {
    if (!isBookingContextReady()) {
        throw createInternalAppError('app context not initialized');
    }
    return _data!.paths;
}

export function getUser(): Undefinedable<AccountsUser> {
    assertIfBookingContextIsReady();
    return _data!.user;
}

export function setUser(user: AccountsUser): void {
    assertIfBookingContextIsReady();
    _data = {
        ..._data!,
        user: {
            ...(user || {})
        }
    };
}

export function getCultureInfoCode(): string {
    assertIfBookingContextIsReady();
    return _data!.cultureInfoCode;
}

export function getVersion(): string {
    assertIfBookingContextIsReady();
    return _data!.version;
}

export function getSiteName() {
    assertIfBookingContextIsReady();
    return _data!.siteName;
}

export function getSiteKey(): string {
    assertIfBookingContextIsReady();
    return _data!.siteKey;
}

export function getIsCaptchaEnable(): boolean {
    assertIfBookingContextIsReady();
    return _data!.isCaptchaEnable;
}

export function getApplicationData(): AccountsContextData {
    if (!isBookingContextReady()) {
        throw createInternalAppError('app context not initialized');
    }
    return _data!;
}